/**
 * The cart.
 *
 * Every write goes straight to Shopify — adding, changing a quantity, editing
 * the note — and nothing on screen changes until the response comes back.
 * There is no local batching and nothing is cached in `localStorage`. An
 * earlier version deferred quantity writes and reconciled them against a
 * cached snapshot on the next page load; that reconciliation was a repeated
 * source of bugs (stale figures after a bfcache restore, drift between tabs,
 * edits replaying against a cart that had already changed underneath them).
 * This version has nothing to get out of sync, at the cost of a request per
 * edit.
 *
 * Writes are still serialised through one queue: Shopify processes concurrent
 * writes against whichever cart state it sees first, so overlapping requests
 * can silently drop a change. A customer clicking a stepper rapidly still
 * fires one request per click, but they land in order.
 *
 * Every write asks for fresh section markup in the same round trip, so a
 * single request updates the drawer, the cart page and the header bubble
 * together. `cart-drawer.ts`, `cart-page.ts` and `cart-count.ts` hold no cart
 * state of their own; they call in here and repaint themselves from whatever
 * section HTML `cart:section` hands them.
 *
 * The one figure this file tracks itself is the header bubble's count, read
 * from `snippets/cart-state-json.liquid` — rendered inside every cart surface
 * so it travels with the section markup — on load and after every write.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A line in a `/cart/*.js` response. */
export interface CartLineItem {
  key: string;
  quantity: number;
}

export interface Cart {
  item_count?: number;
  items?: CartLineItem[];
  /** Present only when the request asked for section rendering. */
  sections?: Record<string, string>;
}

export interface CartStatus {
  /** Authoritative item count, or the optimistic projection of it. */
  count: number;
  /** True while at least one mutation is in flight. */
  busy: boolean;
}

type Listener = (status: CartStatus) => void;

// ---------------------------------------------------------------------------
// Reading the count
// ---------------------------------------------------------------------------

const STATE_SELECTOR = "script[data-cart-state]";

/**
 * Pulled out of raw section HTML rather than a parsed document: responses are
 * handed to us as strings, and this saves building a whole DOM to read one
 * element. The `cart-drawer` and `cart-page` blobs are identical, so the first
 * match in a multi-section response is as good as any.
 */
const STATE_IN_HTML = /<script[^>]*\sdata-cart-state[^>]*>([\s\S]*?)<\/script>/;

/**
 * Defensive on purpose: this blob sits in the same origin as every app on the
 * store, and a malformed one — a theme edit that breaks the JSON, another
 * script's markup — should cost the bubble a stale number, not throw through
 * every caller.
 */
function extractCount(raw: string | null): number | null {
  if (!raw) return null;

  try {
    const data = JSON.parse(raw) as { count?: unknown };
    return typeof data.count === "number" ? data.count : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// The cart
// ---------------------------------------------------------------------------

/** Always re-rendered on a write; it is in `theme.liquid`, so it is on every page. */
const DRAWER_SECTION_ID = "cart-drawer";

/**
 * Marks a write as ours so `cart-write-interceptor.ts` does not treat our own
 * `/cart/add.js` calls as a third-party write and refresh a second time.
 */
export const INTERNAL_WRITE_HEADER = "X-Bondi-Internal-Write";

/** Markets prefix cart routes (`/en-au/cart/add.js`). */
function cartUrl(action: "add" | "update" | "change"): string {
  const root = window.Shopify?.routes?.root ?? "/";
  return `${root.endsWith("/") ? root : `${root}/`}cart/${action}.js`;
}

interface MutationOptions {
  /** Skip the busy state — for writes the customer did not explicitly wait on. */
  silent?: boolean;
  /** Scopes a failure's `cart:error` to one line — set by quantity edits. */
  key?: string;
}

/**
 * Shopify sometimes answers a quantity write with 200 and a cart that was
 * only partially applied, flagging the shortfall in `errors` rather than the
 * response status — e.g. "Only 3 items were added to your cart due to
 * availability." `errors` is a bare string in that shape; other Shopify error
 * responses nest it as `{ field: ["message"] }`, so both are read.
 */
function errorMessage(payload: {
  errors?: string | Record<string, string[]>;
}): string | undefined {
  const { errors } = payload;
  if (typeof errors === "string") return errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors)[0];
    if (Array.isArray(first)) return first[0];
  }
  return undefined;
}

class CartState {
  private initialized = false;
  private status: CartStatus = { count: 0, busy: false };
  private listeners = new Set<Listener>();
  private inFlight = 0;
  /**
   * Cart writes are serialised. Shopify processes concurrent writes against
   * whichever cart state it sees first, so overlapping requests can silently
   * drop a change.
   */
  private queue: Promise<unknown> = Promise.resolve();
  /** In-flight standalone refresh, shared by concurrent callers. */
  private refreshing: Promise<void> | null = null;
  /** Sections to re-render alongside every write; surfaces add their own. */
  private sectionIds = new Set<string>([DRAWER_SECTION_ID]);
  /** Sections defined by a JSON template (the cart page's) need a URL to resolve in. */
  private templateUrl: string | null = null;

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  /**
   * Idempotent — whichever cart surface connects first seeds the count. In
   * practice that is the header bubble, so it is correct before the drawer
   * has even upgraded.
   */
  init(serverCount: number): void {
    if (this.initialized) return;
    this.initialized = true;

    // Liquid rendered this page, so its figure is authoritative for this
    // load. The caller's count is a fallback for a surface that somehow
    // rendered without the state blob.
    const blob = document.querySelector<HTMLScriptElement>(STATE_SELECTOR);
    this.setCount(extractCount(blob?.textContent ?? null) ?? serverCount);
  }

  // -------------------------------------------------------------------------
  // Status
  // -------------------------------------------------------------------------

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.status);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    this.listeners.forEach((listener) => listener(this.status));
  }

  private setCount(count: number): void {
    const next = Math.max(0, count);
    if (next === this.status.count) return;
    this.status = { ...this.status, count: next };
    this.emit();
  }

  private setBusy(busy: boolean): void {
    if (busy === this.status.busy) return;
    this.status = { ...this.status, busy };
    this.emit();
  }

  /**
   * Projects a count change before the server confirms it. Used only while an
   * add is in flight, so the bubble does not sit still for the round trip —
   * quantity edits wait for the real answer instead, see `setLineQuantity`.
   */
  applyOptimisticDelta(delta: number): void {
    if (!delta) return;
    this.setCount(this.status.count + delta);
  }

  // -------------------------------------------------------------------------
  // Quantity edits
  // -------------------------------------------------------------------------

  /**
   * Writes a line's quantity straight to Shopify and waits for the answer.
   * Nothing on screen changes until the response lands and re-renders every
   * registered section — see the module docstring for why.
   */
  async setLineQuantity(key: string, quantity: number): Promise<void> {
    if (!key) return;

    const cart = await this.mutate(
      cartUrl("change"),
      { id: key, quantity },
      { key },
    );
    // Failure — including Shopify answering 200 with an `errors` field — is
    // already reported by performMutation, scoped to this line.
    if (!cart) return;

    // A silent cap: no `errors` field, but the line the response actually
    // sent back differs from what was requested. Almost always inventory.
    const line = cart.items?.find((item) => item.key === key);
    if ((line?.quantity ?? 0) !== quantity) this.reportAdjustments([key], cart);
  }

  // -------------------------------------------------------------------------
  // Syncing
  // -------------------------------------------------------------------------

  /**
   * Makes sure nothing is still writing before checkout starts. There is
   * nothing to flush — every edit already landed on the server as it
   * happened — so this only has to wait for whichever one is still in
   * flight, so checkout never starts mid-write.
   */
  async verifyBeforeCheckout(): Promise<boolean> {
    await this.queue;
    return true;
  }

  async add(body: FormData | Record<string, unknown>): Promise<Cart | null> {
    const result = await this.mutate(cartUrl("add"), body);
    if (result) document.dispatchEvent(new CustomEvent("cart:added"));
    return result;
  }

  async updateNote(note: string): Promise<Cart | null> {
    return this.mutate(cartUrl("update"), { note });
  }

  private mutate(
    url: string,
    body: FormData | Record<string, unknown>,
    options: MutationOptions = {},
  ): Promise<Cart | null> {
    const run = this.queue.then(() => this.performMutation(url, body, options));
    // Keep the chain alive even when a link in it rejects.
    this.queue = run.catch(() => undefined);
    return run;
  }

  private async performMutation(
    url: string,
    body: FormData | Record<string, unknown>,
    options: MutationOptions = {},
  ): Promise<Cart | null> {
    // A background write (e.g. a note debounce) must not dim the drawer or
    // disable the controls the customer is still using.
    const silent = options.silent === true;
    if (!silent) {
      this.inFlight += 1;
      this.setBusy(true);
    }

    try {
      const response = await window.fetch(url, this.requestInit(body));
      const payload = (await response.json()) as Cart & {
        description?: string;
        message?: string;
        errors?: string | Record<string, string[]>;
      };

      const problem = errorMessage(payload);

      if (!response.ok || problem) {
        // Render first: a line-scoped error is written onto the row by its
        // `data-key`, and a render replaces that row's markup wholesale.
        // Dispatching the error before the render would just have it wiped
        // out a moment later.
        if (payload.sections) this.render(payload.sections);
        else await this.refresh();
        this.dispatchError(
          problem ?? payload.description ?? payload.message,
          options.key,
        );
        return null;
      }

      // Sections carry a fresh count, so they answer the bubble on their
      // own. `/cart/add.js` is the exception the other way round: it
      // resolves with the added line rather than the cart, and has no
      // section markup unless `sections` was requested and non-empty.
      if (!this.render(payload.sections)) {
        if (typeof payload.item_count === "number") {
          this.setCount(payload.item_count);
        }
        document.dispatchEvent(
          new CustomEvent("cart:updated", {
            detail: { count: this.status.count },
          }),
        );
      }

      return payload;
    } catch {
      await this.refresh();
      this.dispatchError(undefined, options.key);
      return null;
    } finally {
      if (!silent) {
        this.inFlight -= 1;
        if (this.inFlight === 0) this.setBusy(false);
      }
    }
  }

  /** Every write asks for fresh section markup in the same round trip. */
  private requestInit(body: FormData | Record<string, unknown>): RequestInit {
    const headers: Record<string, string> = {
      Accept: "application/json",
      [INTERNAL_WRITE_HEADER]: "1",
    };
    const sections = Array.from(this.sectionIds).join(",");

    if (body instanceof FormData) {
      body.set("sections", sections);
      if (this.templateUrl) body.set("sections_url", this.templateUrl);
      return { method: "POST", credentials: "same-origin", headers, body };
    }

    headers["Content-Type"] = "application/json";
    return {
      method: "POST",
      credentials: "same-origin",
      headers,
      body: JSON.stringify({
        ...body,
        sections,
        ...(this.templateUrl ? { sections_url: this.templateUrl } : {}),
      }),
    };
  }

  // -------------------------------------------------------------------------
  // Rendering
  // -------------------------------------------------------------------------

  /**
   * Surfaces opt in as they connect, so one request re-renders all of them.
   * The drawer is registered from the start; the cart page adds its own.
   */
  registerSection(id: string, templateUrl?: string): void {
    this.sectionIds.add(id);
    if (templateUrl) this.templateUrl = templateUrl;
  }

  /**
   * Reads the fresh count out of whichever section carries it, and hands each
   * section to whichever surface registered that ID — that surface swaps in
   * the markup itself. Returns false when there was nothing to render, so the
   * caller can fall back to the response JSON.
   */
  private render(sections?: Record<string, string> | null): boolean {
    if (!sections) return false;

    for (const html of Object.values(sections)) {
      const count = extractCount(html.match(STATE_IN_HTML)?.[1] ?? null);
      if (count !== null) {
        this.setCount(count);
        break;
      }
    }

    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      document.dispatchEvent(
        new CustomEvent("cart:section", { detail: { id, html } }),
      );
    });

    document.dispatchEvent(
      new CustomEvent("cart:updated", { detail: { count: this.status.count } }),
    );
    return true;
  }

  /**
   * Re-reads every cart surface in a single request, for when there was no
   * write to piggyback on. Public because `assets/swym-wishlist.js` calls it
   * directly after its SDK writes the cart, and `cart-write-interceptor.ts`
   * calls it after any third-party `/cart/add.js` request it did not make.
   *
   * Concurrent callers share one fetch *and* one paint — painting the same
   * markup twice disturbs focus in an open drawer.
   */
  refresh(): Promise<void> {
    if (this.refreshing) return this.refreshing;

    const base = this.templateUrl ?? "/";
    const separator = base.includes("?") ? "&" : "?";
    const ids = Array.from(this.sectionIds).join(",");

    this.refreshing = window
      .fetch(`${base}${separator}sections=${ids}`, {
        credentials: "same-origin",
      })
      .then((response) => (response.ok ? response.json() : null))
      .then((sections: Record<string, string> | null) => {
        this.render(sections);
      })
      // Offline or blocked — leave the last known state in place.
      .catch(() => undefined)
      .finally(() => {
        this.refreshing = null;
      });

    return this.refreshing;
  }

  // -------------------------------------------------------------------------
  // Errors
  // -------------------------------------------------------------------------

  private dispatchError(message?: string, key?: string): void {
    document.dispatchEvent(
      new CustomEvent("cart:error", {
        detail: {
          message: message || "Something went wrong. Please retry.",
          key,
        },
      }),
    );
  }

  /**
   * Puts an inline note on each line the server corrected. Copy comes from the
   * drawer markup rather than a translation key, because `locales/*.json` is
   * gitignored in this theme and additions there cannot be committed.
   */
  private reportAdjustments(keys: string[], cart: Cart): void {
    const drawer = document.querySelector<HTMLElement>("cart-drawer");
    const adjustedTemplate =
      drawer?.dataset.msgAdjusted ?? "Only [[ quantity ]] available.";
    const unavailable =
      drawer?.dataset.msgUnavailable ?? "This item is no longer available.";

    keys.forEach((key) => {
      const line = cart.items?.find((item) => item.key === key);
      document.dispatchEvent(
        new CustomEvent("cart:error", {
          detail: {
            key,
            message: line
              ? adjustedTemplate.replace(
                  "[[ quantity ]]",
                  String(line.quantity),
                )
              : unavailable,
          },
        }),
      );
    });
  }
}

export const cartState = new CartState();

declare global {
  interface Window {
    Shopify?: { routes?: { root?: string } };
    /** `openDrawer` is attached by `cart-write-interceptor.ts`. */
    BondiCart?: CartState & { openDrawer?: () => void };
  }
}

// Exposed so non-module code can nudge the cart after writing it directly.
// `assets/swym-wishlist.js` calls `refresh()` this way as a fallback for when
// its SDK captured `fetch` before `cart-write-interceptor.ts` patched it —
// that interceptor is what notices third-party writes in the normal case.
window.BondiCart = cartState;
